import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { CmsEntities } from 'client/data/models/cms';
import { Link } from 'site-modules/shared/components/link/link';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { CardsCarousel } from 'site-modules/editorial/components/car-news/cards-carousel/cards-carousel';
import { AdEntry } from 'site-modules/editorial/components/ad-entry/ad-entry';
import { Spotlight } from 'site-modules/editorial/components/car-news/spotlight/spotlight';
import { VideoCard } from 'site-modules/editorial/components/car-news/video-card/video-card';
import { NewsCard } from 'site-modules/editorial/components/car-news/news-card/news-card';
import { InlineNewsCard } from 'site-modules/editorial/components/car-news/inline-news-card/inline-news-card';
import { CarNewsArticlesGrid } from 'site-modules/editorial/components/car-news/car-news-articles-grid/car-news-articles-grid';

export const RENDER_TYPE = {
  ARTICLES_GRID: 'articles-grid',
  ARTICLES_CAROUSEL: 'articles-carousel',
  VIDEOS: 'videos',
  COL_SECTION: 'col-section',
  HTML: 'html',
  AD: 'ad',
};

export function excludeSpotlight({ id }) {
  return id !== 'spotlight-video';
}

export function ResearchEntry({
  entry,
  isChild = false,
  setVideoModalData = noop,
  isLazyLoadImages = false,
  isMobile = false,
}) {
  const renderType = entry.metadata('renderType').value();

  let renderEntry;
  switch (renderType) {
    case RENDER_TYPE.ARTICLES_GRID: {
      const buttonLink = entry.metadata('buttonLink').value();
      const buttonLinkText = entry.metadata('buttonLinkText').value();

      renderEntry = (
        <Fragment>
          <CarNewsArticlesGrid
            className={classnames({ 'mb-2': buttonLink })}
            content={entry}
            isMobile={isMobile}
            isLazyLoadImages={isLazyLoadImages}
          />
          {!!buttonLink && (
            <div className="text-center">
              <Button
                tag={Link}
                to={buttonLink}
                size="lg"
                color="blue-50"
                className="py-0_75 px-1 fw-medium text-transform-none"
                style={{ minWidth: '270px' }}
              >
                {buttonLinkText}
              </Button>
            </div>
          )}
        </Fragment>
      );
      break;
    }
    case RENDER_TYPE.ARTICLES_CAROUSEL: {
      renderEntry = (
        <CardsCarousel
          content={entry}
          isMobile={isMobile}
          CardComponent={isMobile ? NewsCard : InlineNewsCard}
          isLazyLoadImages={isLazyLoadImages}
          showArrows={!isMobile}
          centerMode={isMobile}
        />
      );
      break;
    }
    case RENDER_TYPE.VIDEOS: {
      const spotlightVideo = entry.child('spotlight-video');

      renderEntry = (
        <Fragment>
          {!!spotlightVideo && (
            <div style={{ marginBottom: isMobile ? '2rem' : '5.875rem' }}>
              <Spotlight
                content={spotlightVideo}
                openVideoModal={setVideoModalData}
                isLazyLoadImages={isLazyLoadImages}
              />
            </div>
          )}
          <div style={{ maxHeight: isMobile ? '275px' : '245px', overflow: 'hidden' }}>
            <CardsCarousel
              content={entry}
              CardComponent={VideoCard}
              openVideoModal={setVideoModalData}
              childrenFilter={excludeSpotlight}
              isLazyLoadImages={isLazyLoadImages}
              showArrows
              centerMode={false}
            />
          </div>
        </Fragment>
      );
      break;
    }
    case RENDER_TYPE.COL_SECTION: {
      const moreLink = entry.metadata('moreLink').value();
      const moreLinkText = entry.metadata('moreLinkText').value();
      const moreLinkTrackingId = entry.metadata('moreLinkTrackingId').value();

      renderEntry = (
        <Row>
          <Col xs={12} lg={7}>
            {entry.children().map(childEntry => (
              <ResearchEntry
                key={childEntry.id}
                entry={childEntry}
                isMobile={isMobile}
                isLazyLoadImages={isLazyLoadImages}
                isChild
              />
            ))}
            {!!moreLink && (
              <Link className="text-primary-darker" to={moreLink} data-tracking-id={moreLinkTrackingId}>
                {moreLinkText}
                <i className="icon-arrow-right4 small d-inline-block ms-0_25" aria-hidden />
              </Link>
            )}
          </Col>
        </Row>
      );
      break;
    }
    case RENDER_TYPE.HTML: {
      renderEntry = <ContentFragment>{entry.metadata('htmlContent').value()}</ContentFragment>;
      break;
    }
    case RENDER_TYPE.AD: {
      renderEntry = <AdEntry entry={entry} isMobile={isMobile} />;
      break;
    }
    default: {
      break;
    }
  }

  if (!renderEntry) {
    return null;
  }

  const { title } = entry;
  const anchor = entry.metadata('anchor').value();
  const creativeId = entry.metadata('creativeId').value();
  const highlighted = entry.metadata('highlighted').boolean();

  return isChild ? (
    <div className="mb-1_5">{renderEntry}</div>
  ) : (
    <ScrollElement
      id={anchor || entry.id}
      className={classnames('mb-3', { 'py-3 bg-blue-100': highlighted })}
      data-tracking-parent={creativeId}
    >
      <Container className="px-lg-0">
        {!!title && <h2 className="heading-3 mb-2">{title}</h2>}
        {renderEntry}
      </Container>
    </ScrollElement>
  );
}

ResearchEntry.propTypes = {
  entry: CmsEntities.Content.isRequired,
  setVideoModalData: PropTypes.func,
  isChild: PropTypes.bool,
  isLazyLoadImages: PropTypes.bool,
  isMobile: PropTypes.bool,
};
